import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PrivateRoute from './shared/components/PrivateRoute';
import PublicRoute from './shared/components/PublicRoute';
import Layout from './shared/components/Layout';
import LoadingSpinner from './shared/components/LoadingSpinner';

const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage'));
const ConfirmPage = lazy(() => import('./features/auth/ConfirmPage'));
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage'));
const BabySetupPage = lazy(() => import('./features/babies/BabySetupPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const FeedPage = lazy(() => import('./features/feeding/FeedPage'));
const SleepPage = lazy(() => import('./features/sleep/SleepPage'));
const NappyPage = lazy(() => import('./features/nappy/NappyPage'));
const ActivityPage = lazy(() => import('./features/activity/ActivityPage'));
const PumpingPage = lazy(() => import('./features/pumping/PumpingPage'));
const IllnessListPage = lazy(() => import('./features/illness/IllnessListPage'));
const IllnessDetailPage = lazy(() => import('./features/illness/IllnessDetailPage'));
const GrowthPage = lazy(() => import('./features/growth/GrowthPage'));
const MilestonePage = lazy(() => import('./features/growth/MilestonePage'));
const VaccinationPage = lazy(() => import('./features/growth/VaccinationPage'));
const ReportsPage = lazy(() => import('./features/reports/ReportsPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const MorePage = lazy(() => import('./features/more/MorePage'));
const SafetyHubPage = lazy(() => import('./features/safety/SafetyHubPage'));
const SafeSleepPage = lazy(() => import('./features/safety/SafeSleepPage'));
const FirstAidPage = lazy(() => import('./features/safety/FirstAidPage'));
const EmergencyInfoPage = lazy(() => import('./features/safety/EmergencyInfoPage'));
const MedicationPage = lazy(() => import('./features/medication/MedicationPage'));
const AllergyPage = lazy(() => import('./features/allergy/AllergyPage'));
const TempAccessPage = lazy(() => import('./features/access/TempAccessPage'));
const InvitePage = lazy(() => import('./features/access/InvitePage'));
const MoodPage = lazy(() => import('./features/mood/MoodPage'));
const UVIndexPage = lazy(() => import('./features/safety/UVIndexPage'));
const SubscriptionPage = lazy(() => import('./features/subscription/SubscriptionPage'));
const TipsPage = lazy(() => import('./features/tips/TipsPage'));
const TipDetailPage = lazy(() => import('./features/tips/TipDetailPage'));
const OAuthCallbackPage = lazy(() => import('./features/auth/OAuthCallbackPage'));
const NotFoundPage = lazy(() => import('./features/auth/NotFoundPage'));
// v1.5.0 — onboarding, paywall, trial offer and referral surfaces.
const WelcomePage = lazy(() => import('./features/onboarding/WelcomePage'));
const OnboardingPage = lazy(() => import('./features/onboarding/OnboardingPage'));
const OnboardingDonePage = lazy(() => import('./features/onboarding/OnboardingDonePage'));
const FirstRunGate = lazy(() => import('./features/onboarding/FirstRunGate'));
const PaywallPage = lazy(() => import('./features/subscription/PaywallPage'));
const OfferPage = lazy(() => import('./features/offer/OfferPage'));
const PassLandingPage = lazy(() => import('./features/referral/PassLandingPage'));

function SuspenseWrapper({ children }) {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
      {children}
    </Suspense>
  );
}

export default function AppRoutes() {
  return (
    <SuspenseWrapper>
      <Routes>
        <Route element={<PublicRoute />}>
          {/* Signed-out landing (PrivateRoute and sign-out send people here). */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Bare: the first steps run signed out, the page owns its auth boundary. */}
        <Route path="/onboarding" element={<OnboardingPage />} />

        <Route element={<PrivateRoute />}>
          {/* Authenticated, OUTSIDE the shell: full-screen pages with no nav chrome. */}
          <Route path="/baby-setup" element={<BabySetupPage />} />
          <Route path="/onboarding/done" element={<OnboardingDonePage />} />
          <Route path="/paywall" element={<PaywallPage />} />
          <Route path="/offer" element={<OfferPage />} />
          {/* FirstRunGate sends an account with no baby (and no pending invite)
              into onboarding once the babies query has settled. */}
          <Route element={<FirstRunGate />}>
          <Route element={<Layout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/sleep" element={<SleepPage />} />
            <Route path="/nappy" element={<NappyPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/pumping" element={<PumpingPage />} />
            <Route path="/illness" element={<IllnessListPage />} />
            <Route path="/illness/:illnessId" element={<IllnessDetailPage />} />
            <Route path="/growth" element={<GrowthPage />} />
            <Route path="/milestones" element={<MilestonePage />} />
            <Route path="/vaccinations" element={<VaccinationPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/safety" element={<SafetyHubPage />} />
            <Route path="/safety/safe-sleep" element={<SafeSleepPage />} />
            <Route path="/safety/first-aid" element={<FirstAidPage />} />
            <Route path="/safety/emergency-info" element={<EmergencyInfoPage />} />
            <Route path="/medications" element={<MedicationPage />} />
            <Route path="/allergies" element={<AllergyPage />} />
            <Route path="/mood" element={<MoodPage />} />
            <Route path="/safety/uv" element={<UVIndexPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/tips" element={<TipsPage />} />
            <Route path="/tips/:tipId" element={<TipDetailPage />} />
            <Route path="/more" element={<MorePage />} />
          </Route>
          </Route>
        </Route>

        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/access/:token" element={<TempAccessPage />} />
        {/* Signed-out on purpose — the invitee usually has no account yet. */}
        <Route path="/invite/:token" element={<InvitePage />} />
        {/* Signed-out on purpose — a referral pass link lands here from a message. */}
        <Route path="/pass/:code" element={<PassLandingPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </SuspenseWrapper>
  );
}
